import moment from 'moment';
import { Inject } from '@odinjs/odin';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AutocompleteAPI, FetchAPI, FormAPI, InputAPI, ModalService, TextAPI } from '@philips-internal/oswald-ui-components';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import BaseController from '../../../../BaseController';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import { ApplicationRoute } from '../../../../../constants/routes';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import MedicationService from '../../../services/medication-service';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import { InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { ReasonType } from '../../adult-tpn/adult-tpn-types';
import { FragmentNames, GastherapySuspendAliases } from '../../../../../codegen';
import GastherapyService from '../gastherapy-service';

@Controller({ component: GastherapySuspendAliases.GASTHERAPY_SUSPEND_LAYOUT, fragment: FragmentNames.GASTHERAPY_SUSPEND })
export default class NursingInterventionsSuspendController extends BaseController {

  @Inject(GastherapySuspendAliases.FORM_REASON)
  private formApi!: FormAPI;

  @Inject(GastherapySuspendAliases.AUTOCOMPLETE_ITEM_SUSPEND_REASON)
  private reasonAutocompleteApi!: AutocompleteAPI

  @OnMount(GastherapySuspendAliases.DATE_HOUR_INPUT)
  handleDateFieldMount(event: MountEvent): void {
    const api = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    api.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnMount([GastherapySuspendAliases.TEXT_GASTHERAPY_DATE, GastherapySuspendAliases.TEXT_GASTHERAPY_INFORMATION])
  handleMountTexts({ target }: EventData<TextAPI>): void {
    const data = TimelineContext.get() as TimeLineEventMedication;
    const value = target.getValue()
      .replace(':gastherapy-information', data.description as string)
      .replace(':gastherapy-date', DateFormatterUtil.timeStamp(String(data.start)));
    target.setValue(value);
  }

  @OnMount(GastherapySuspendAliases.FETCH_GASTHERAPY_ITEM_REASON)
  handleSuspendFetchReasonMount(event: MountEvent): void {
    const fetchSuspendReason = event.target as FetchAPI;
    fetchSuspendReason.setParameters({ ieTypeEvent: 'S' }, { skip: true });
  }

  @OnBeforeCancel(GastherapySuspendAliases.FORM_REASON)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnBeforeSubmit(GastherapySuspendAliases.FORM_REASON)
  handleSubmitForm(): void {
    this.suspend();
  }

  private suspend(): void {
    const currentRecord = this.formApi.getRecord();
    const timelineGastherapy = TimelineContext.get() as TimeLineEventMedication;
    const infoGastherapy = this.context.getBulkData();
    const dtChange = moment(String(currentRecord.currentTime)).format(ISO_FULL_DATE_FORMAT);
    const justification = currentRecord.justification
    const reason = this.reasonAutocompleteApi.getValue() as unknown as ReasonType
    this.formApi.setLoading(true)
    const body = {
      device: null,
      dtChange,
      ieChange: String(InfusionItemStatus.SUSPEND),
      justification,
      nrSeqReason: String(reason.sequenceNumber),
      prescriptionNumber: Number(MedicineAndSolutionsParams.params.nrPrescriptionId),
      qtGastherapy: Number(currentRecord.quantity),
      scheduleSeq: timelineGastherapy.id,
      seqGastherapy: timelineGastherapy.itemSequence,
      seqGastherapyCpoe: timelineGastherapy.cpoeSequence,
      unitOfMeasurement: infoGastherapy.unitOfMeasurement
    } as Record<string, Value>

    GastherapyService.generateGasTherapyChange(body)
      .then(() => {
        MedicationService.showToast(MessageService.translate('txt.emar.item-suspend'), ToastLevel.SUCCESS)
        this.redirectUserToTimeline();
        this.formApi.setLoading(false);
      })
      .catch((error) => {
        const message = error.body.messageDescription;
        const errorMessage = error.body.errors.join('; ');
        MedicationService.showToast(message ?? errorMessage, ToastLevel.ERROR);
      });
  }

  private redirectUserToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    ModalService.close(FragmentNames.GASTHERAPY_SUSPEND);
    ModalService.close(FragmentNames.GASTHERAPY_VIEWER);
  }
}